import React from 'react';
import styled from 'styled-components';
import { PatternFormat, NumericFormat } from 'react-number-format';

import { Input } from 'antd';
import { Field, ErrorMessage, FieldAttributes, FieldProps } from 'formik';
import TextArea from 'antd/es/input/TextArea';
import { PasswordRegex } from 'config';

const Wrapper = styled.tr`
  color: ${(props) => props.theme.colors.primary};
  font-size: 1rem;

  @media only screen and (max-width: 768px) {
    .labelContainer {
      display: block;
    }
  }

  td {
    padding: 5px 10px;
    vertical-align: top;

    &:first-child {
      font-weight: 500;
      padding-top: 8px;
    }

    input,
    textarea {
      border: 1px solid ${(props) => props.theme.colors.primary};
      width: 100%;
      font-size: 1rem;
      background-color: #ffffff;
      color: ${(props) => props.theme.colors.primary};
      border-radius: 0;
      padding: 5px 10px;
    }

    .error {
      font-size: 0.8rem;
      color: ${(props) => props.theme.colors.red};
      margin-bottom: -5px;
    }
  }
`;

const TextField: React.FC<FieldAttributes<any>> = ({
  label,
  inputType = 'input',
  labelStyle = {},
  inputStyle = {},
  type = 'text',
  readonly = false,
  format = '###-###-####',
  rows = 3,
  isCheckPasswordString = false,
  callback = () => {},
  ...props
}) => {
  const renderInput = ({
    field: { name, value, onBlur },
    form: { setFieldValue },
  }: FieldProps) => {
    if (inputType === 'input') {
      return (
        <Input
          {...props}
          value={value}
          onBlur={onBlur}
          type={type}
          name={name}
          onChange={(v) => {
            setFieldValue(name, v.target.value);
            if (isCheckPasswordString) {
              if (!PasswordRegex.test(v.target.value)) {
                callback(false);
              } else {
                callback(true);
              }
            }
          }}
          placeholder={props.placeholder}
          readOnly={readonly}
          style={{ ...inputStyle }}
        />
      );
    }

    if (inputType === 'textarea') {
      return (
        <TextArea
          {...props}
          value={value}
          onBlur={onBlur}
          name={name}
          rows={rows}
          onChange={(v) => setFieldValue(name, v.target.value)}
          placeholder={props.placeholder}
        />
      );
    }

    if (inputType === 'mask') {
      return (
        <PatternFormat
          mask="_"
          format={format}
          {...props}
          value={value}
          onBlur={onBlur}
          name={name}
          onValueChange={(values) => {
            setFieldValue(name, values.formattedValue);
          }}
          placeholder={props.placeholder}
          customInput={Input}
        />
      );
    }

    if (inputType === 'number') {
      return (
        <NumericFormat
          thousandSeparator=","
          decimalScale={2}
          prefix="$"
          {...props}
          value={value}
          onBlur={onBlur}
          name={name}
          onValueChange={(values) => {
            setFieldValue(name, values.floatValue);
          }}
          customInput={Input}
        />
      );
    }

    return null;
  };

  return (
    <Wrapper>
      <td className="labelContainer" style={{ minWidth: 150, ...labelStyle }}>
        {label}
      </td>
      <td style={{ display: 'flex', width: '100%' }}>
        <Field {...props}>
          {(fieldProps: FieldProps) => renderInput(fieldProps)}
        </Field>
        <ErrorMessage name={props.name} component="div" className="error" />
      </td>
    </Wrapper>
  );
};

export default TextField;
